import './SettingsPage.css';
import React, { useContext, useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { SettingsContext } from '../SettingsContext';
import { v4 as uuidv4 } from 'uuid';
import { toast } from 'react-toastify';
import { getAuthHeaders } from '../funcs/authHeaders';

const API_BASE_URL = process.env.REACT_APP_BACKEND_URL;
const exchangesList = ['binance', 'lbank', 'htx', 'mexc', 'gate', 'bybit', 'bingx', 'bitget', 'ourbit', 'kucoin'];

const SettingsPage = () => {
  const { settings, setSettings, telegramId } = useContext(SettingsContext);
  const [templateName, setTemplateName] = useState('');
  const [templates, setTemplates] = useState([]);
  const [selectedTemplateId, setSelectedTemplateId] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const navigate = useNavigate();

  // Добавляем useEffect для работы с Telegram WebApp API
  useEffect(() => {
    if (window.Telegram.WebApp) {
      window.Telegram.WebApp.enableClosingConfirmation();
      window.Telegram.WebApp.BackButton.show();
      window.Telegram.WebApp.BackButton.onClick(() => {
        navigate('/futures-screen');
      });
    }
    
    return () => {
      if (window.Telegram.WebApp) {
        window.Telegram.WebApp.BackButton.hide();
      }
    };
  }, [navigate]);

  // Загрузка шаблонов при монтировании компонента
  const effectiveTelegramId = telegramId || localStorage.getItem('fallback_telegram_id');

  useEffect(() => {
    if (effectiveTelegramId) {
      fetchTemplates();
    }
  }, [effectiveTelegramId]);

  const fetchTemplates = async () => {
    try {
      const url = effectiveTelegramId
        ? `${API_BASE_URL}/templates?telegram_id=${effectiveTelegramId}`
        : `${API_BASE_URL}/templates`;
      const response = await fetch(url, { headers: getAuthHeaders() });
      const data = await response.json();
      setTemplates(data || []);
    } catch (err) {
      console.error('Error fetching templates:', err.message);
      toast.error('Ошибка при загрузке шаблонов.');
    }
  };

  const handleExchangeChange = (e) => {
    const { name, checked } = e.target;
    setSettings((prevSettings) => ({
      ...prevSettings,
      exchanges: {
        ...prevSettings.exchanges,
        [name]: checked,
      },
    }));
  };

  const handleTokenBlacklistChange = (e) => {
    setSettings((prevSettings) => ({
      ...prevSettings,
      tokenBlacklist: e.target.value,
    }));
  };

  const handleTokenWhitelistChange = (e) => {
    setSettings((prevSettings) => ({
      ...prevSettings,
      tokenWhitelist: e.target.value,
    }));
  };

  const handleMinProfitChange = (e) => {
    let value = parseFloat(e.target.value);
    // if (isNaN(value) || value < 0.5) value = 0.5;
    setSettings((prevSettings) => ({
      ...prevSettings,
      minProfit: value,
    }));
  };

  const handleMaxProfitChange = (e) => {
    let value = parseFloat(e.target.value);
    // if (isNaN(value) || value < 0.6) value = 0.6;
    setSettings((prevSettings) => ({
      ...prevSettings,
      maxProfit: value,
    }));
  };

  const handleMinVolumeChange = (e) => {
    let value = parseFloat(e.target.value);
    if (isNaN(value) || value < 0) value = 0;
    setSettings((prevSettings) => ({
      ...prevSettings,
      minVolume: value,
    }));
  };

  const handleExchangeBlacklistChange = (exchange, type, e) => {
    setSettings((prevSettings) => ({
      ...prevSettings,
      exchangeBlacklists: {
        ...prevSettings.exchangeBlacklists,
        [exchange]: {
          ...prevSettings.exchangeBlacklists[exchange],
          [type]: e.target.value,
        },
      },
    }));
  };

  // Сохранение шаблона
  const saveTemplate = async () => {
    if (!templateName?.trim()) {
      setError('Пожалуйста, введите название шаблона.');
      return;
    }

    if (templates.length >= 5) {
      setError('Максимум 5 шаблон��в.');
      return;
    }

    try {
      setLoading(true);
      setError('');

      // Проверка уникальности названия
      const isNameTaken = templates.some(
        (template) => template.name.toLowerCase() === templateName.trim().toLowerCase()
      );

      if (isNameTaken) {
        setError('Шаблон с таким названием уже существует.');
        setLoading(false);
        return;
      }

      // Создание нового шаблона
      const newTemplate = {
        id: uuidv4(),
        name: templateName?.trim(),
        settings: settings,
      };

      const response = await fetch(`${API_BASE_URL}/templates`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify({ telegram_id: effectiveTelegramId, template: newTemplate }),
      });

      if (!response.ok) throw new Error('Ошибка при сохранении шаблона');

      setTemplates((prev) => [...prev, newTemplate]);
      setTemplateName('');
      toast.success('Шаблон успешно сохранён!');
    } catch (err) {
      console.error('Error saving template:', err.message);
      setError('Ошибка при сохранении шаблона.');
      toast.error('Ошибка при сохранении шаблона.');
    } finally {
      setLoading(false);
    }
  };

  // Загрузка шаблона
  const loadTemplate = async (templateId) => {
    try {
      setLoading(true);
      setError('');

      const selectedTemplate = templates.find((template) => template.id === templateId);
      if (!selectedTemplate) {
        setError('Выбранный шаблон не найден.');
        setLoading(false);
        return;
      }

      setSettings(selectedTemplate.settings);
      setSelectedTemplateId(templateId);

      // Уведомление
      toast.success(`Шаблон "${selectedTemplate.name}" загружен!`);
    } catch (err) {
      console.error('Error loading template:', err.message);
      setError('Ошибка при загрузке шаблона.');
      toast.error('Ошибка при загрузке шаблона.');
    } finally {
      setLoading(false);
    }
  };

  // Удаление шаблона
  const deleteTemplate = async (templateId) => {
    try {
        setLoading(true);
        setError('');

        const response = await fetch(`${API_BASE_URL}/templates/delete`, {
            method: 'POST',
            headers: getAuthHeaders(),
            body: JSON.stringify({
                telegram_id: effectiveTelegramId,
                templateId: templateId,
            }),
        });

        if (!response.ok) {
            const errorDetails = await response.text();
            throw new Error(`Ошибка при удалении шаблона. Ответ сервера: ${errorDetails}`);
        }

        await response.json();

        // Обновление списка шаблонов
        setTemplates((prev) => prev.filter((template) => template.id !== templateId));

        if (selectedTemplateId === templateId) {
            setSelectedTemplateId('');
        }

        toast.success('Шаблон успешно удалён!');
    } catch (err) {
        console.error('Error deleting template:', err.message, err.stack);
        setError('Ошибка при удалении шаблона.');
        toast.error('Ошибка при удалении шаблона.');
    } finally {
        setLoading(false);
    }
  };

  return (
    <div className='SettingsPageWrapper'>
      {/* <Link to="/futures-screen">
        <button className='settingsElementWrapper btn-back'>
          <svg xmlns="http://www.w3.org/2000/svg" width="13" height="13" viewBox="0 0 13 13" fill="none">
          <path d="M10.2913 6.5H2.70801" stroke="black" strokeWidth="1.08333" strokeLinecap="round" strokeLinejoin="round"/>
          <path d="M6.49967 10.2918L2.70801 6.50016L6.49967 2.7085" stroke="black" strokeWidth="1.08333" strokeLinecap="round" strokeLinejoin="round"/>
          </svg>
          Назад
        </button>
      </Link> */}
      
      <h2>Фильтры поиска</h2>

      {/* Секция управления шаблонами */}
      <div className='settingsElementWrapper'>
        <h3>Управление шаблонами фильтров</h3>

        {/* Выбор существующего шаблона */}
        <div>
          <h4>Выбрать шаблон фильтров</h4>
          <select
            value={selectedTemplateId}
            onChange={(e) => loadTemplate(e.target.value)}
            disabled={loading || templates.length === 0}
            style={{ marginRight: '8px' }}
          >
            <option style={{color: '#323232'}} value="">Выберите шаблон</option>
            {templates.map((template) => (
              <option key={template.id} value={template.id}>
                {template.name}
              </option>
            ))}
          </select>
          {/* Кнопка удаления шаблона */}
          {selectedTemplateId && (
            <button
              onClick={() => deleteTemplate(selectedTemplateId)}
              disabled={loading}
              className='btn-delete'
            >
              Удалить
            </button>
          )}
        </div>

        {/* Сохранение нового шаблона */}
        <div>
          <h4>Сохранить текущие настройки</h4>
          <input
            type="text"
            value={templateName}
            onChange={(e) => setTemplateName(e.target.value)}
            placeholder="Введите название шаблона"
            style={{ marginRight: '8px' }}
          />
          <button className='btn-save-template' onClick={saveTemplate} disabled={loading}>
            Сохранить
          </button>
          {error && <p style={{ color: 'red' }}>{error}</p>}
        </div>
      </div>

      {/* Остальная часть настроек */}
      <div className='settingsElementWrapper'>
        <h3>Выберите биржи</h3>
        <div className='exchanges-list'>
          {exchangesList.map((exchange) => (
            <label key={exchange} style={{ display: 'block', marginBottom: '8px' }}>
              <input
                type="checkbox"
                name={exchange}
                checked={settings.exchanges[exchange]}
                onChange={handleExchangeChange}
              />
            <span className="checkbox-indicator"></span>
            {exchange}
          </label>
        ))}
        </div>
      </div>

      <div className='settingsElementWrapper coins-list'>
        <div className=''>
          <h3>Черный список монет</h3>
          <input
            type="text"
            value={settings.tokenBlacklist}
            onChange={handleTokenBlacklistChange}
            placeholder="Введите монеты через запятую"
            style={{ width: '100%', padding: '6px 10px' }}
          />
        </div>

        <div className=''>
          <h3>Белый список монет</h3>
          <input
            type="text"
            value={settings.tokenWhitelist}
            onChange={handleTokenWhitelistChange}
            placeholder="Введите монеты через запятую"
            style={{ width: '100%', padding: '6px 10px' }}
          />
        </div>
      </div>

      <div className='settingsElementWrapper profits_volume'>
        <div className='profits'>
          <div>
            <h3>Мин. профит <span className='minProfitSpan'>(%)</span></h3>
            <input
              type="number"
              value={settings.minProfit}
              onChange={handleMinProfitChange}
              min="0.5"
              style={{ width: '100%', padding: '8px' }}
            />
          </div>

          <div>
            <h3>Макс. профит <span className='minProfitSpan'>(%)</span></h3>
            <input
              type="number"
              value={settings.maxProfit}
              onChange={handleMaxProfitChange}
              min="0.6"
              style={{ width: '100%', padding: '8px' }}
            />
          </div>
        </div>

        <div>
          <h3>Минимальный объем <span className='minProfitSpan'>($)</span></h3>
          <input
            type="number"
            value={settings.minVolume}
            onChange={handleMinVolumeChange}
            min="0"
            style={{ width: '100%', padding: '8px' }}
          />
        </div>
      </div>

      {/* Секция настройки уведомлений */}
      <div className='settingsElementWrapper'>
        <h3>Настройки звуковых уведомлений</h3>

        <div>
          <h4>Мин. спред для звукового уведомления:</h4>
          <input
            type="number"
            value={settings.minSpreadValueForSound}
            onChange={(e) => setSettings((prevSettings) => ({
              ...prevSettings,
              minSpreadValueForSound: parseFloat(e.target.value) || 0
            }))}
            style={{ width: '100%', padding: '8px' }}
          />
        </div>
      </div>

      <h2>Черный список по биржам</h2>

      <div>
        {exchangesList.map((exchange) => (
          <div className='settingsElementWrapper'>
            <div key={exchange}>
              <h3>{`по ${exchange[0].toUpperCase() + exchange.slice(1)}:`}</h3>
              <div>
                <h4>Покупка:</h4>
                <input
                  type="text"
                  value={settings.exchangeBlacklists[exchange]?.buy}
                  onChange={(e) => handleExchangeBlacklistChange(exchange, 'buy', e)}
                  placeholder="Введите монеты через запятую"
                  style={{ width: '100%', padding: '8px' }}
                />
              </div>
              <div>
                <h4>Продажа:</h4>
                <input
                  type="text"
                  value={settings.exchangeBlacklists[exchange]?.sell}
                  onChange={(e) => handleExchangeBlacklistChange(exchange, 'sell', e)}
                  placeholder="Введите монеты через запятую"
                  style={{ width: '100%', padding: '8px' }}
                />
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default SettingsPage;
